// START GENAI@GHCOPILOT
import { Component, h, Prop, State, Event, EventEmitter, Method, Watch } from '@stencil/core';
import { DropdownInputAttr, KeyValue } from '@src/components/vs-form-builder/components/dropdown/vs-dropdown.model';
import { InputAttr } from '@src/components/vs-form-builder/components/input/vs-input.model';
import { SENTOSA_DATA_KEY_TYPE } from '@src/workflows/vs-standard-form/helpers/auth.helper';
import { arraysAreEqual } from '@utils/utils';
import { CityClubList, CountryCityList } from '@src/workflows/vs-standard-form/vs-standard-form.constant';


@Component({
  tag: 'vs-sentosa-dropdown',
  shadow: false,
})

export class VsSentosaDropdown {

  /**
   * @shortDescription Identifier of the input field.
   * @controlName inputId */
  @Prop() inputId: string;

  /**
   * @shortDescription Index of input field in a form.
   * @controlName index */
  @Prop() index: number;


  /**
   * @shortDescription Input's type
   * @controlName type */
  @Prop() type: string = 'text';

  /**
   * @shortDescription Define the label for input field.
   * @controlName label */
  @Prop() label: string;

  /**
   * @shortDescription Label exists in the input field as eyebrow view.
   * @controlName eyebrow */
  @Prop() eyebrow: boolean = false;

  /**
   * @shortDescription If declared, it is the default option index.
   * @controlName defaultValue */
  @Prop() defaultValue: string = '';

  /**
   * @shortDescription Validators rules to check against for the input.
   * @controlName validators */
  @Prop() validators: any[] = [];

  /**
   @shortDescription required text label
   @controlName requiredText
   */
  @Prop() requiredText: string;

  /**
   @shortDescription placeholder for the dropdown input
   @controlName placeholder
   */
  @Prop() placeholder: string = 'Please select';

  /* START GENAI@GHCOPILOT */
  /**
   * @shortDescription HTML content to be inserted before the form field
   * @controlName aboveInputContent
   */
  @Prop() aboveInputContent: string;

  /**
   * @shortDescription HTML content to be inserted after the form field
   * @controlName belowInputContent
   */
  @Prop() belowInputContent: string;

  /**
   * @shortDescription HTML content to be inserted after the form field
   * @controlName betweenInputContent
   */
  @Prop() betweenInputContent: string;

  /**
   @shortDescription country list for dropdown display
   @controlName ctryCityList
   */
  @Prop() ctryCityList: CountryCityList[];

  /**
   @shortDescription city list for dropdown display
   @controlName cityClubList
   */
  @Prop() cityClubList: CityClubList[];

  /**
   @shortDescription display options based on selectedValue from parent component
   @controlName selectedValue
   */
  @Prop() dependsOnValues: string[] | null;


  /* END GENAI@GHCOPILOT */

  @State() showError: boolean = false;
  @State() value: string = '';
  @State() selectedErrorMessage: string = '';
  @State() valid: boolean = true;
  @State() inputAllyAlert: string = 'input_alert_';
  @State() isRequired: boolean;
  @State() processedData: KeyValue[] = [];

  /**
   * @shortDescription Emit Input blur event to parent component on input blur
   * @controlName blurEvent */
  @Event() blurEvent: EventEmitter<void>;

  sentosaInputEl?;

  /**
   * @shortDescription Emit input value on change
   * @controlName computedInputValue */
  @Event({
    eventName: 'computedInputValue',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) computedInputValue: EventEmitter;

  /**
   * @shortDescription Reset value when reset button is clicked
   * @controlName resetValue */
  @Method()
  async resetValue(isShowError = false) {
    this.sentosaInputEl.resetValue(isShowError);
  }

  /**
   * @shortDescription set input focus
   * @controlName setFocus */
  @Method()
  async setFocus() {
    this.sentosaInputEl.setFocus();
  }

  /* START GENAI@GHCOPILOT */
  @Watch('dependsOnValues')
  dependsOnValue(newValue: string[], oldValue: string[]) {
    if (!arraysAreEqual(oldValue, newValue)) {
      this.populateDropdownOptionsBasedOnOptionKey(newValue);
    }
  }

  onComputedInputValue = (e: CustomEvent) => {
    const value = e.detail as InputAttr;
    this.computedInputValue.emit(
      {
        ...value,
        index: this.index,
        key: this.inputId,
      } as DropdownInputAttr,
    );
  };

  componentWillLoad() {
    this.populateDropdownOptionsBasedOnOptionKey(this.dependsOnValues);
  }

  private mapListToKeyValuePairs = (list: any) => {
    return list?.map((value: KeyValue) => ({ key: value?.value, value: value?.value })) || [];
  };

  populateDropdownOptionsBasedOnOptionKey = (dependsOnValues: any) => {
    const { country, city, club } = SENTOSA_DATA_KEY_TYPE;

    switch (this.inputId) {
      case country:
        this.processedData = [...Object.keys(this.ctryCityList).map((value: string) => ({ key: value, value: value }))];
        break;
      case city:
        this.processedData
          = [...this.mapListToKeyValuePairs(this.ctryCityList[dependsOnValues?.[0]]?.cityList)];
        break;
      case club:
        this.processedData
          = [...this.mapListToKeyValuePairs(this.cityClubList[`${dependsOnValues?.[0]}${dependsOnValues?.[1]}`]?.clubList)];
        break;
      default:
        this.processedData = [];
        break;
    }
    const populateOptionTimeOut = setTimeout(() => {
      clearTimeout(populateOptionTimeOut);
      if(this.sentosaInputEl) {
        this.sentosaInputEl.data = [...this.processedData];
      }
    }, 10);
  };

  render() {
    return (
      <vs-dropdown
        ref={el => this.sentosaInputEl = el as HTMLElement}
        inputId={this.inputId}
        index={this.index}
        eyebrow={this.eyebrow}
        label={this.label}
        placeholder={this.placeholder}
        requiredText={this.requiredText}
        data={this.processedData}
        aboveInputContent={this.aboveInputContent}
        betweenInputContent={this.betweenInputContent}
        belowInputContent={this.belowInputContent}
        onComputedInputValue={this.onComputedInputValue}
        validators={this.validators}
      />
    );
  };
}
// END GENAI@GHCOPILOT
